import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useNavigate, useParams } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { useAuthContext } from "@/contexts/AuthContext"
import { useStoreDetail } from "@/graphql/hooks/useStoreDetail"
import { useCountries, useRegions, useLocations } from "@/graphql/hooks/Locations/useLocations"
import { StoreEditInput, useStoreEdit } from "@/graphql/hooks/useStoreEdit"
import { useAllGames } from "@/graphql/hooks/useGames"
import { useAllTournamentTypes } from "@/graphql/hooks/useAllTournamentTypes"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ArrowLeft, Store, Calendar } from "lucide-react"
import { StoreAdminSection } from "@/components/admin/StoreAdminSection"

const storeEditSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  inChargePerson: z.string().optional(),
  description: z.string().optional(),
  phone: z.string().min(8, "El teléfono debe tener al menos 8 caracteres"),
  email: z.string().email("Email inválido"),
  siteUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  facebookUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  instagramUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  youtubeUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  twitterUrl: z.string().url("URL inválida").optional().or(z.literal("")),
  active: z.boolean().optional(),
  clasificationId: z.number().optional(),
  levelId: z.number().optional(),
  tournamentDays: z.array(z.number()).optional(),
  storeGames: z.array(z.number()).optional(),
  tournamentTypes: z
    .array(
      z.object({
        typeId: z.number(),
        quantity: z.number().min(1, "La cantidad debe ser mayor a 0"),
      }),
    )
    .optional(),
  address: z.object({
    id: z.number().optional(),
    name: z
      .string()
      .min(2, "El nombre de la dirección es requerido")
      .optional()
      .or(z.literal("Casa matriz")),
    address: z.string().min(5, "La dirección es requerida"),
    countryId: z.number().min(1, "El país es requerido"),
    regionId: z.number().min(1, "La región es requerida").optional().or(z.literal(0)).or(z.nan()),
    locationId: z.number().min(1, "La comuna es requerida").optional().or(z.nan()).or(z.literal(0)),
  }),
})

type StoreEditFormValues = z.infer<typeof storeEditSchema>

const tournamentDays = [
  { id: 1, name: "Lunes" },
  { id: 2, name: "Martes" },
  { id: 3, name: "Miércoles" },
  { id: 4, name: "Jueves" },
  { id: 5, name: "Viernes" },
  { id: 6, name: "Sábado" },
  { id: 7, name: "Domingo" },
]

// storeClassifications and storeLevels moved to StoreAdminSection component

export const EditStore = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { person, isAuthenticated, isAdmin, loading } = useAuthContext()
  const { editStore, loading: editLoading } = useStoreEdit()

  const storeId = id ? parseInt(id) : 0
  const skipStoreDetail = !storeId || editLoading
  const { store, loading: storeLoading } = useStoreDetail(storeId, {
    skip: skipStoreDetail,
  })

  const [selectedCountry, setSelectedCountry] = useState(0)

  const { games } = useAllGames(false)
  const { tournamentTypes } = useAllTournamentTypes()

  const { countries } = useCountries()
  const { regions } = useRegions(selectedCountry)
  const { locations, refetchLocations } = useLocations()

  const [tournamentTypeQuantities, setTournamentTypeQuantities] = useState<Record<number, number>>(
    {},
  )

  const form = useForm<StoreEditFormValues>({
    resolver: zodResolver(storeEditSchema),
    defaultValues: {
      name: "",
      inChargePerson: "",
      description: "",
      phone: "",
      email: "",
      siteUrl: "",
      facebookUrl: "",
      instagramUrl: "",
      youtubeUrl: "",
      twitterUrl: "",
      active: true,
      clasificationId: 1,
      levelId: 1,
      tournamentDays: [],
      storeGames: [],
      tournamentTypes: [],
      address: {
        id: 0,
        name: "",
        address: "",
        countryId: 0,
        regionId: 0,
        locationId: 0,
      },
    },
  })

  useEffect(() => {
    if (!loading) {
      if (!storeLoading && !isAuthenticated) {
        navigate("/login")
        return
      }

      if (!storeLoading && !isAdmin && (!person?.Store?.id || person.Store.id !== storeId)) {
        navigate("/profile")
        return
      }
    }
  }, [isAuthenticated, person, storeId, navigate, isAdmin, storeLoading, loading])

  useEffect(() => {
    if (store && !storeLoading) {
      // Set tournament type quantities
      const quantities: Record<number, number> = {}
      store.StoreTournamentTypes?.forEach((stt) => {
        quantities[stt.typeId] = stt.quantity
      })
      setTournamentTypeQuantities(quantities)

      form.reset({
        name: store.name || "",
        inChargePerson: store.inChargePerson || "",
        description: store.description || "",
        phone: store.phone || "",
        email: store.email || "",
        siteUrl: store.siteUrl || "",
        facebookUrl: store.facebookUrl || "",
        instagramUrl: store.instagramUrl || "",
        youtubeUrl: store.youtubeUrl || "",
        twitterUrl: store.twitterUrl || "",
        active: store.active === 1,
        clasificationId: store.clasificationId || 1,
        levelId: store.levelId || 1,
        tournamentDays: store.StoreTournamentDays?.map((std) => std.TournamentDay.id) || [],
        storeGames: store.StoreGames?.map((sg) => sg.gameId) || [],
        tournamentTypes:
          store.StoreTournamentTypes?.map((stt) => ({
            typeId: stt.typeId,
            quantity: stt.quantity,
          })) || [],
        address: {
          id: store.Address?.id || 0,
          name: store.Address?.name || "",
          address: store.Address?.address || "",
          countryId: store.Address?.countryId || 0,
          regionId: store.Address?.regionId || 0,
          locationId: store.Address?.locationId || 0,
        },
      })

      // Refetch regions and locations when store data is loaded
      if (store.Address?.countryId) {
        // refetchRegions(store.Address.countryId);
      }
      if (store.Address?.regionId) {
        refetchLocations(store.Address.regionId)
      }
    }
  }, [store, storeLoading, form])

  const onSubmit = async (values: StoreEditFormValues) => {
    try {
      const tournamentTypesData = Object.entries(tournamentTypeQuantities)
        .filter(([_, quantity]) => quantity > 0)
        .map(([typeId, quantity]) => ({
          typeId: parseInt(typeId),
          quantity,
        }))

      const storeInput: StoreEditInput = {
        id: storeId,
        name: values.name!,
        inChargePerson: values.inChargePerson,
        description: values.description,
        phone: values.phone!,
        email: values.email!,
        active: values.active ? 1 : 0,
        clasificationId: values.clasificationId,
        levelId: values.levelId,
        siteUrl: values.siteUrl,
        facebookUrl: values.facebookUrl,
        instagramUrl: values.instagramUrl,
        youtubeUrl: values.youtubeUrl,
        twitterUrl: values.twitterUrl,
        tournamentDays: values.tournamentDays,

        address: {
          id: values.address!.id,
          name: values.address!.name,
          address: values.address!.address,
          countryId: values.address!.countryId,
          regionId: values.address!.regionId || 0,
          locationId: values.address!.locationId || 0,
        },
      }

      if (isAdmin) {
        storeInput.storeGames = values.storeGames
        storeInput.tournamentTypes = tournamentTypesData
      }

      const result = await editStore(storeInput)
      if (result?.StoreEdit?.status?.success) {
        navigate("/profile")
        return
      }
    } catch (error) {
      console.error("Error editing store:", error)
    }
  }

  if (storeLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">Cargando...</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  if (!store) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No se encontró la tienda</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate("/profile")} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Perfil
          </Button>

          <div className="flex items-center gap-3 mb-2">
            <Store className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Editar Tienda</h1>
          </div>
          <p className="text-muted-foreground">Actualiza la información de la tienda.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Información de la Tienda</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre de la Tienda *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="inChargePerson"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Persona Encargada</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email *</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Redes Sociales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="siteUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Sitio Web</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="facebookUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Facebook</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://facebook.com/..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="instagramUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Instagram</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://instagram.com/..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="twitterUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Twitter</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="https://twitter.com/..." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Dirección</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="address.address"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Dirección *</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Av. Principal 123" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address.countryId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>País *</FormLabel>
                          <Select
                            value={field.value?.toString()}
                            onValueChange={(value) => {
                              // refetchRegions(Number(value));
                              setSelectedCountry(Number(value))
                              return field.onChange(parseInt(value))
                            }}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar país" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {countries?.map((country) => (
                                <SelectItem key={country.id} value={country.id.toString()}>
                                  {country.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address.regionId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Región *</FormLabel>
                          <Select
                            value={field.value?.toString()}
                            onValueChange={(value) => {
                              refetchLocations(parseInt(value))
                              return field.onChange(parseInt(value))
                            }}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar región" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {regions?.map((region) => (
                                <SelectItem key={region.id} value={region.id.toString()}>
                                  {region.fullName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address.locationId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Comuna</FormLabel>
                          <Select
                            value={field.value?.toString()}
                            onValueChange={(value) => field.onChange(parseInt(value))}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccionar comuna" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {locations?.map((location) => (
                                <SelectItem key={location.id} value={location.id.toString()}>
                                  {location.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Tournament Days Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold">Días de Torneos</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {tournamentDays.map((day) => (
                      <FormField
                        key={day.id}
                        control={form.control}
                        name="tournamentDays"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(day.id)}
                                onCheckedChange={(checked) => {
                                  const current = field.value || []
                                  if (checked) {
                                    field.onChange([...current, day.id])
                                  } else {
                                    field.onChange(current.filter((id) => id !== day.id))
                                  }
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal">{day.name}</FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>

                {/* Admin Only Section */}
                {isAdmin && (
                  <StoreAdminSection
                    control={form.control}
                    games={games.map((g) => ({ ...g, isActive: g.isActive === 1 }))}
                    tournamentTypes={tournamentTypes}
                    tournamentTypeQuantities={tournamentTypeQuantities}
                    setTournamentTypeQuantities={setTournamentTypeQuantities}
                  />
                )}

                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => (isAdmin ? navigate("/stores") : navigate("/profile"))}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={editLoading} className="flex-1">
                    {editLoading ? "Guardando..." : "Guardar Cambios"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
